import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/firebase-config';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Trash2, Upload, CheckCircle2, AlertCircle, HelpCircle } from 'lucide-react';
import './SliderManager.css';

const SliderManager = () => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  
  // Metin Alanları State'leri
  const [mainTitle, setMainTitle] = useState('Modern Tesisler');
  const [greenTitle, setGreenTitle] = useState('Tam Otomatik Üretim');
  const [description, setDescription] = useState('');
  const [buttonText, setButtonText] = useState('Ürünlerimizi Keşfet');

  // Bildirim ve Onay Durumları
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });

  const showPopup = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const fetchSliders = async () => {
    try {
      const q = query(collection(db, "sliders"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      setImages(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Veri çekme hatası:", err);
    }
  };

  useEffect(() => { fetchSliders(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return showPopup("Lütfen bir görsel seçiniz.", "error");
    
    setUploading(true);
    try {
      const storageRef = ref(storage, `sliders/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, "sliders"), { 
        url,
        mainTitle,
        greenTitle,
        description,
        buttonText,
        createdAt: new Date()
      });
      
      setFile(null);
      setMainTitle('Modern Tesisler'); 
      setGreenTitle('Tam Otomatik Üretim'); 
      setDescription('');
      setButtonText('Ürünlerimizi Keşfet');
      fetchSliders();
      showPopup("Slider ve metinler başarıyla eklendi.", "success");
    } catch (err) { 
      showPopup("İşlem sırasında bir hata oluştu.", "error");
    }
    setUploading(false);
  };

  // Silme Onay Penceresini Açan Fonksiyon
  const openDeleteModal = (id) => {
    setDeleteModal({ show: true, id });
  };

  // Gerçek Silme İşlemini Yapan Fonksiyon
  const handleDelete = async () => {
    const id = deleteModal.id;
    try {
      await deleteDoc(doc(db, "sliders", id));
      setDeleteModal({ show: false, id: null });
      fetchSliders();
      showPopup("Görsel başarıyla silindi.", "success");
    } catch (err) {
      showPopup("Silme işlemi başarısız oldu.", "error");
    }
  };

  return (
    <div className="slider-manager-container">
      {/* Bildirim Popup'ı */}
      {notification.show && (
        <div className={`notification-popup ${notification.type}`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Özel Onay Modalı */}
      {deleteModal.show && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <HelpCircle size={45} color="#d4a373" />
            <h3>Silme Onayı</h3>
            <p>Bu slider içeriğini kalıcı olarak silmek istediğinizden emin misiniz?</p>
            <div className="confirm-btns">
              <button className="cancel-btn" onClick={() => setDeleteModal({ show: false, id: null })}>Vazgeç</button>
              <button className="confirm-delete-btn" onClick={handleDelete}>Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Veri Giriş Formu */}
      <form className="upload-section custom-form" onSubmit={handleUpload}>
        <div className="form-grid">
          <div className="input-group full">
             <label>Slider Görseli</label>
             <div className="file-input-wrapper">
               <Upload size={24} className="upload-icon" />
               <input type="file" id="file-upload" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
               <label htmlFor="file-upload">{file ? file.name : "Görsel Seçin veya Sürükleyin"}</label>
             </div>
          </div>
          <div className="input-group">
            <label>Ana Başlık (Beyaz)</label>
            <input type="text" value={mainTitle} onChange={(e) => setMainTitle(e.target.value)} placeholder="Örn: Modern Tesisler" />
          </div>
          <div className="input-group">
            <label>Vurgulu Başlık (Yeşil)</label>
            <input type="text" value={greenTitle} onChange={(e) => setGreenTitle(e.target.value)} placeholder="Örn: Tam Otomatik Üretim" />
          </div>
          <div className="input-group full">
            <label>Açıklama Metni</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Slider üzerindeki açıklama..." />
          </div>
          <div className="input-group">
            <label>Buton Yazısı</label>
            <input type="text" value={buttonText} onChange={(e) => setButtonText(e.target.value)} />
          </div>
        </div>
        
        <button type="submit" className="upload-confirm-btn" disabled={uploading}>
          {uploading ? <div className="spinner-small"></div> : "İçeriği Yayınla"}
        </button>
      </form>

      <div className="divider"><span>Yayındaki Slider İçerikleri</span></div>

      <div className="slider-preview-grid">
        {images.length > 0 ? (
          images.map((img) => (
            <div key={img.id} className="preview-card-full">
              <img src={img.url} alt="Slider" />
              <div className="card-details">
                <h4>{img.mainTitle} <span className="green">{img.greenTitle}</span></h4>
                <p>{img.description}</p>
                <button onClick={() => openDeleteModal(img.id)} className="delete-btn-simple">
                  <Trash2 size={16}/> Kaldır
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-data">Henüz eklenmiş bir içerik bulunmuyor.</p>
        )}
      </div>
    </div>
  );
};

export default SliderManager;